import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext, useEffect, useState, useRef } from 'react';
import { Alert, AlertTitle, Typography, Box, Button, Link, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import { Link as RouteLink } from 'react-router';
import { DelayedLoadIndicator } from '../../../components/delayed-load-indicator';
import { Timestamp } from '../../../components/timestamp';
import { MainContext } from '../../../context';
import { DeleteIconButton } from '../../../components/delete-icon-button';
import { DetailsCard } from '../../../components/details-card';
import { GenerateAccessTokenDialog } from './generate-access-token-dialog';
import { TrustedPublishingPromo } from '../trusted-publishing/trusted-publishing-promo';
import { EmptyPlaceholder, IconTile } from '../settings/settings-primitives';
import { SettingsHeader } from '../settings/settings-header';
import { UserSettingsRoutes } from '../user-settings-routes';
/** Confirmation copy for the pending deletion. */
const deletePrompt = (target) => target === 'all'
    ? {
        title: 'Delete all access tokens',
        message: 'Are you sure you want to delete all access tokens?',
        confirmLabel: 'Delete'
    }
    : {
        title: 'Revoke access token',
        message: `Revoking ${target.description ? `“${target.description}”` : 'this token'} cannot be undone. Any command line or CI workflow still using it will fail to publish until you replace it.`,
        confirmLabel: 'Revoke'
    };
const TokenRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1.125rem 1.25rem'
});
export const UserSettingsTokens = () => {
    const { service, user, handleError, pageSettings } = useContext(MainContext);
    const [tokens, setTokens] = useState(new Array());
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState();
    const abortController = useRef(new AbortController());
    useEffect(() => {
        updateTokens();
        return () => {
            abortController.current.abort();
        };
    }, []);
    const updateTokens = async () => {
        if (!user) {
            return;
        }
        try {
            const tokens = await service.getAccessTokens(abortController.current, user);
            setTokens(tokens);
            setLoading(false);
        }
        catch (err) {
            handleError(err);
            setLoading(false);
        }
    };
    const confirmDelete = async (target) => {
        setDeleteTarget(undefined);
        setLoading(true);
        try {
            if (target === 'all') {
                await service.deleteAllAccessTokens(abortController.current, tokens);
            }
            else {
                await service.deleteAccessToken(abortController.current, target);
            }
            updateTokens();
        }
        catch (err) {
            handleError(err);
        }
    };
    const handleTokenGenerated = () => {
        setLoading(true);
        updateTokens();
    };
    const renderToken = (token) => {
        return (_jsxs(TokenRow, { children: [_jsx(IconTile, { sx: { width: '2.25rem', height: '2.25rem' }, children: _jsx(KeyOutlinedIcon, { sx: { fontSize: '1.0625rem' } }) }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { noWrap: true, sx: {
                                fontSize: '0.90625rem',
                                fontWeight: 600
                            }, children: token.description }), token.scopeNamespace || token.publishingOnly ? (_jsx(Typography, { sx: { fontSize: '0.78125rem', color: 'text.disabled', mt: '0.125rem' }, children: [
                                token.scopeNamespace
                                    ? `Scope: ${token.scopeNamespace}${token.scopeExtension ? `.${token.scopeExtension}` : ''}`
                                    : undefined,
                                token.publishingOnly ? 'Publishing only' : undefined
                            ]
                                .filter(Boolean)
                                .join(' · ') })) : null, _jsxs(Typography, { sx: { fontSize: '0.78125rem', color: 'text.disabled', mt: '0.125rem' }, children: ["Expires", ' ', token.expiresTimestamp ? (_jsx(Timestamp, { value: token.expiresTimestamp, isFutureTime: true })) : ('never')] }), _jsxs(Typography, { sx: { fontSize: '0.75rem', color: 'text.disabled', mt: '0.125rem' }, children: ["Created ", _jsx(Timestamp, { value: token.createdTimestamp }), token.accessedTimestamp ? (_jsxs(_Fragment, { children: [' ', "\u00B7 Last used ", _jsx(Timestamp, { value: token.accessedTimestamp })] })) : null] })] }), _jsx(DeleteIconButton, { title: 'Revoke token', "aria-label": token.description ? `Revoke token ${token.description}` : 'Revoke token', sx: { flexShrink: 0 }, onClick: () => setDeleteTarget(token), disabled: loading })] }, 'token:' + token.id));
    };
    const agreement = user?.publisherAgreement;
    if (agreement && (agreement.status === 'none' || agreement.status === 'outdated')) {
        const publisherAgreementContact = pageSettings?.publisherAgreement?.email;
        // The deployment may not name its agreement, so drop the empty slot rather than double-spacing.
        const agreementName = [pageSettings?.publisherAgreement?.name, 'Publisher Agreement'].filter(Boolean).join(' ');
        return (_jsxs(Box, { children: [_jsx(SettingsHeader, { title: 'Access Tokens', description: 'Personal access tokens let you publish extensions from the command line. Treat them like passwords.' }), _jsxs(Alert, { severity: 'warning', children: [_jsx(AlertTitle, { sx: { fontWeight: 700 }, children: "Publisher agreement required" }), "Tokens can only be created once you have signed the ", agreementName, ".", _jsx(Box, { sx: { mt: '0.75rem' }, children: _jsxs(Link, { component: RouteLink, to: UserSettingsRoutes.PROFILE, children: ["Sign it on your profile", _jsx(ArrowForwardIcon, { sx: { fontSize: '1.125rem' } })] }) }), publisherAgreementContact !== undefined && (_jsxs(Box, { sx: { mt: '0.75rem', fontSize: '0.8125rem' }, children: ["If you believe this is in error, contact", ' ', _jsx(Link, { href: `mailto:${publisherAgreementContact}`, children: publisherAgreementContact }), "."] }))] })] }));
    }
    return (_jsxs(Box, { children: [_jsx(SettingsHeader, { title: 'Access Tokens', description: 'Personal access tokens let you publish extensions from the command line. Treat them like passwords.', actions: _jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'error', onClick: () => setDeleteTarget('all'), disabled: loading || tokens.length === 0, children: "Delete all" }), _jsx(GenerateAccessTokenDialog, { handleTokenGenerated: handleTokenGenerated })] }) }), _jsx(Box, { mb: '1.375rem', children: _jsx(TrustedPublishingPromo, {}) }), _jsx(DelayedLoadIndicator, { loading: loading }), tokens.length > 0 ? (_jsx(DetailsCard, { children: tokens.map(token => renderToken(token)) })) : !loading ? (_jsx(EmptyPlaceholder, { children: "You currently have no tokens." })) : null, deleteTarget ? (_jsx(DeleteTokenDialog, { target: deleteTarget, onClose: () => setDeleteTarget(undefined), onConfirm: () => confirmDelete(deleteTarget) })) : null] }));
};
const DeleteTokenDialog = ({ target, onClose, onConfirm }) => {
    const { title, message, confirmLabel } = deletePrompt(target);
    return (_jsxs(Dialog, { open: true, onClose: onClose, children: [_jsx(DialogTitle, { children: title }), _jsx(DialogContent, { children: _jsx(DialogContentText, { component: 'div', children: message }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: onClose, children: "Cancel" }), _jsx(Button, { variant: 'contained', color: 'error', autoFocus: true, onClick: onConfirm, children: confirmLabel })] })] }));
};
//# sourceMappingURL=user-settings-tokens.js.map